import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { Typography, FormControlLabel, RadioGroup, Radio, Tooltip, Button, Box, Switch } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import { AppContext } from '/providers/app-context.js'
import { urlsMatchIgnoringCacheBust } from '/components/editor/image-url'
import PopupUtils from '/utils/popup-editor-utils'
import ImageSelector from '/components/image-selector'
import PopupShell from '../popup-shell'

const canDelete = {
  "meta.images.icon": false,
  "meta.images.logo": false,
  "meta.images.thumbnail": true,
  "meta.images.strip": true,
  "meta.images.stampcard": false,
  "meta.images.background": true,
}

const variablesPaths = {
  "meta.images.icon": "images.apple.icon",
  "meta.images.logo": "images.apple.logo",
  "meta.images.thumbnail": "images.apple.thumbnail",
  "meta.images.strip": "images.apple.strip",
  "meta.images.stampcard": "images.apple_strip",
  "meta.images.background": "images.apple.background",
}

const variableBoundImagePaths = new Set([
  "meta.images.thumbnail",
  "meta.images.strip",
  "meta.images.background",
])

const PopupDialogImage = ({ open, close, title, path, stampcardUrl, saveFunction }) => {

  const { template, variables, setTemplate, setVariables, passType, openVariablesManager, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)
  const { imageList } = useContext(AppContext)

  const [newTemplate, setNewTemplate] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [dataSource, setDataSource] = useState("instance")
  const [requiredSize, setRequiredSize] = useState("")
  const [stampCardUrl, setStampCardUrl] = useState(stampcardUrl ?? "")

//  const permitVariable = stampcardUrl ? false :  true
  const permitVariable = false

  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])
  const imageCatalogEntry = catalogMap[variablesPaths[path]]
  const instanceValueReadonly = imageCatalogEntry?.editable !== undefined && Number(imageCatalogEntry?.editable) === 0

  const imageType = useMemo(() => path.split(".").pop(), [path])
  const showTitleText = useMemo(() => _.get(newTemplate, "meta.title") !== " ", [newTemplate])

  const currentImageUrl = useMemo(() => {
    if (!open) return null

    if (stampcardUrl !== undefined) return stampCardUrl

    let initialImageUrl = dataSelector({ template: newTemplate, variables: newVariables, path })
    const img = imageList.find(i => urlsMatchIgnoringCacheBust(i.s3_url, initialImageUrl))
    return img ? img.s3_url : initialImageUrl
  }, [open,newTemplate, newVariables, path, stampCardUrl, stampcardUrl, imageList])

  // Runs on "open" state change or if template / instance data change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      setNewTemplate(_.cloneDeep(template))
      setNewVariables(_.cloneDeep(variables))
      setStampCardUrl(stampcardUrl ?? "")

      let tv = _.get(template, path)
//      let source = tv?.toString().match(/^{{.*}}$/) ? "instance" : "template"
      const source = "instance"
      setDataSource(source)
    }
    catch (err) {
      console.error(err)
    }
  }, [open, stampcardUrl, template, variables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateNewTemplate = value => {
    const datapath = path
    PopupUtils.updateTemplate({ newTemplate, setNewTemplate, datapath, value })
  }

  const updateVariables = value => {
    const datapath = path
    PopupUtils.updateVariables({ newTemplate, newVariables, setNewVariables, datapath, value })
  }

  const updateBoth = (instanceValue, templateValue) => {
    let i = _.cloneDeep(newVariables)
    let t = _.cloneDeep(newTemplate)

    if (instanceValue) _.set(i, variablesPaths[path], instanceValue)
    else _.unset(i, variablesPaths[path])
    
    _.set(t, path, templateValue)

    setNewVariables(i)
    setNewTemplate(t)
  }

  const handleChangeDataSource = value => { // "instance" or "template"
    if (value === "instance" && instanceValueReadonly) return

    setDataSource(value)

    // Now swap data around to support the new datasource choice

    if (value === "instance") { // template -> instance
      // copy template value to instance data. Insert instance path into template
      const imageUrl = _.get(newTemplate, path)
      updateBoth(imageUrl, `{{${variablesPaths[path]}}}`)
    } else { // instance -> template
      // copy instance data into template. Set instance data to null
      const imageUrl = _.get(newVariables, variablesPaths[path])
      updateBoth(null, imageUrl)
    }
  }

  const removeImageFromTemplate = () => {
    PopupUtils.deleteDataItem({ source: newTemplate, updater: setNewTemplate, path })

    if (variableBoundImagePaths.has(path)) {
      setNewVariables(prev => {
        const nextVariables = _.cloneDeep(prev)
        _.unset(nextVariables, variablesPaths[path])
        return nextVariables
      })
    }
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {
    // Save content of our "new" template/variables back to state

    if (saveFunction) { // Used for stampcards
      saveFunction(stampCardUrl)
      close()
    } else {

      if (!_.isEqual(newTemplate, template)) {
        let nt = { ...newTemplate }
        setTemplate(nt)
      }

      if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
      close()
    }
  }

  const setImageUrl = imgUrl => {
    if (saveFunction) {
      setStampCardUrl(imgUrl)
    } else {
      if (variableBoundImagePaths.has(path) && !instanceValueReadonly) {
        updateBoth(imgUrl, `{{${variablesPaths[path]}}}`)
        setDataSource("instance")
      } else if (dataSource === "template")
        updateNewTemplate(imgUrl)
      else if (!instanceValueReadonly)
        updateVariables(imgUrl)
    }
  }

  const handleToggleTitleText = enabled => {
    setNewTemplate(prev => {
      const nextTemplate = _.cloneDeep(prev)
      _.set(nextTemplate, "meta.title", enabled ? "{{pass.title}}" : " ")
      return nextTemplate
    })
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose} width={1000} variant="solid">
        {/* Header */}
        <div className="flex-shrink-0 p-2">
          <div className="flex justify-between items-center">
            <Typography color="secondary" variant="h6">{title}<br/>({requiredSize})</Typography>

            {permitVariable && (
              <RadioGroup row value={dataSource} onChange={e => handleChangeDataSource(e.target.value)}>
                <FormControlLabel value="template" control={<Radio />} label="Same image for all passes" />
                <FormControlLabel value="instance" control={<Radio />} label="Use unique images per pass" disabled={instanceValueReadonly && dataSource !== "instance"} />
              </RadioGroup>
            )}

            {path === "meta.images.logo" && (
              <div className="flex items-center gap-2">
                <Typography variant="body2">Hide</Typography>
                <Switch checked={showTitleText} onChange={e => handleToggleTitleText(e.target.checked)} />
                <Typography variant="body2">Show title text</Typography>
              </div>
            )}

            <Tooltip title="Press ESC to dismiss">
              <Button endIcon={<EastRoundedIcon />} onClick={handleClose}>Cancel</Button>
            </Tooltip>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-5 p-2">
          <ImageSelector
            passType={passType}
            currentImageUrl={currentImageUrl}
            setImageUrl={setImageUrl}
            imageType={imageType}
            deleteAllowed={canDelete[path]}
            removeImageFromTemplate={removeImageFromTemplate}
            setRequiredSize={setRequiredSize}
          />
        </div>

        {/* Footer */}
        <Box className="flex-shrink-0 p-3">
          <hr className="my-2" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
            
            {path === "meta.images.background" && (
              <div className="text-xs p-3">
                NOTE: Foreground text is always <b>WHITE</b> on background images.
                <b>This cannot be changed</b>. Use a dark image so text is visible.
              </div>
            )}
            <div />
            <Button variant="contained" onClick={saveFormData}>Save</Button>
          </div>
        </Box>
      </PopupShell>
    )
  }
  catch (err) {
    console.error(err)
  }
}

export default PopupDialogImage
